import { PingCacheDuration } from "@/constants/global";
import {
  AemRoleIds,
  AffiliationHeadRoleIds,
  AllAdminRoleIds,
  AllManagerRoleIds,
  AllStudentRoleIds,
  AllTeacherRoleIds,
} from "@/constants/userRoles";
import { LoginIdTypesEnum } from "@/enums/global";
import { StudentRoleIdsEnum } from "@/enums/userManagement";
import { gotoLogout, showCustomMessage } from "@/methods/utils";
import router from "@/routerInstance";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useOfflineDownloadStore } from "@/stores/core/offlineDownloadStore";
import { useServerClockStore } from "@/stores/core/serverClockStore";
import { useStudentStore } from "@/stores/student/studentStore";
import {
  ApiResponse,
  LoginWithAccessToken,
  LoginWithOtp,
  LoginWithPassword,
  RegisterB2CUser,
  SessionStore,
  UserAlert,
  UserInfo,
} from "@/types/global";
import { menuController } from "@ionic/vue";
import { HttpStatusCode } from "axios";
import { defineStore, storeToRefs } from "pinia";
import { useIonModalStore } from "./ionModalStore";

function getInitialSessionStore(): SessionStore {
  const defaultUserInfo: UserInfo = {
    UserId: 0,
    UserName: "",
    EmailId: "",
    Mobile: "",
    RoleId: StudentRoleIdsEnum.STUDENT,
    AffiliationId: 0,
    AffiliationTypeId: 0,
    IsMultiAffiliation: false,
    IsDemoUser: false,
    UserDashboard: "",
    SessionUuid: "",
    AccessToken: null,
    RefreshToken: null,
    LoginSessionId: 0,
    CommunicationEmail: null,
    CommunicationMobile: null,
    ShowPasswordWarning: false,
    MustChangePassword: false,
  };
  const initialSessionStore: SessionStore = {
    UserInfo: defaultUserInfo,
    UserAlerts: null,
    LastPingStatusCode: null,
    LastPingTimestamp: null,
    LastAccessedTime: null,
    DuplicateSessionInterval: null,
    LastDuplicateSessionChecked: null,
    ActiveCustomToasts: [],
  };

  return JSON.parse(JSON.stringify(initialSessionStore));
}

export const useSessionStore = defineStore("sessionStore", {
  state: (): SessionStore => getInitialSessionStore(),
  getters: {
    IsLoggedIn(): boolean {
      return this.UserInfo.AccessToken !== null;
    },
    IsStudent(): boolean {
      return Object.values(AllStudentRoleIds).includes(this.UserInfo.RoleId);
    },
    IsMustChangePasswordActive(): boolean {
      // BR-UI-101/102: non-student users with MustChangePassword are forced to /change-password;
      // student roles (11–17, incl. B2C) are exempt from the gate.
      return this.UserInfo.MustChangePassword && !this.IsStudent;
    },
    IsManagerRole(): boolean {
      return AllManagerRoleIds.includes(this.UserInfo.RoleId);
    },
    IsAdminRole(): boolean {
      return AllAdminRoleIds.includes(this.UserInfo.RoleId);
    },
    IsAemRole(): boolean {
      return AemRoleIds.includes(this.UserInfo.RoleId);
    },
    IsAffiliationHead(): boolean {
      return AffiliationHeadRoleIds.includes(this.UserInfo.RoleId);
    },
    IsTeacherRole(): boolean {
      return AllTeacherRoleIds.includes(this.UserInfo.RoleId);
    },
  },
  actions: {
    async beforeNavigateStart() {
      const serverClockStore = useServerClockStore();
      const { pingServerAndInitializeClock, getServerTimestamp } = serverClockStore;
      const latestTimestamp = await getServerTimestamp();
      const isCacheValid =
        this.LastPingTimestamp !== null &&
        this.LastPingStatusCode === HttpStatusCode.Ok &&
        latestTimestamp - this.LastPingTimestamp < PingCacheDuration;
      if (isCacheValid) {
        return;
      }
      this.LastPingStatusCode = await pingServerAndInitializeClock();
      this.LastPingTimestamp = latestTimestamp;
    },
    async afterNavigateValidated() {
      await useIonModalStore().dismissModal();
      await this.closeIonMenus();
      await useAppSettingsStore().deactivateAppFullscreen();
      await useAppSettingsStore().setAppScreenOrientation();
      await Promise.all(this.ActiveCustomToasts.map((toast) => toast?.dismiss?.()).filter(Boolean));
      this.ActiveCustomToasts = [];
      this.hideAppLoader();
    },
    async validateLoggedInSession() {
      const isDuplicateSession = await this.checkDuplicateLoginSession();
      if (isDuplicateSession) {
        return false;
      }
      if (!this.IsLoggedIn) {
        return true;
      }
      if (this.IsStudent) {
        const studentStore = useStudentStore();
        await studentStore.getEnrolledCourses(true);
      }
      this.ActiveCustomToasts = [];
      const appSettingsStore = useAppSettingsStore();
      const { IsCustomDomain, CustomAssets } = storeToRefs(appSettingsStore);
      const isValidUserInfo = this.UserInfo.UserId !== 0;
      const isValidCustomDomain =
        !IsCustomDomain.value ||
        (CustomAssets.value !== null &&
          CustomAssets.value.CustomDomain === window.location.hostname);
      if (!(isValidCustomDomain && isValidUserInfo)) {
        await gotoLogout(true);
        return false;
      }
      if (
        appSettingsStore.AppMenu !== null &&
        appSettingsStore.AppMenu.RoleId !== this.UserInfo.RoleId
      ) {
        appSettingsStore.resetMenus();
      }
      await appSettingsStore.getNavbarMenu();
      await this.initDuplicateSessionChecker();
      this.getUserAlerts();
      return true;
    },
    async initLoginWithPassword(loginCredentials: LoginWithPassword) {
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.loginWithPassword(loginCredentials);
      await this.processLoginResponse(response);
    },
    async initLoginWithOtp(loginCredentials: LoginWithOtp) {
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.loginWithOtp(loginCredentials);
      await this.processLoginResponse(response);
    },
    async initLoginWithThirdPartyAccessToken(loginCredentials: LoginWithAccessToken) {
      const appSettingsStore = useAppSettingsStore();
      const { ApiBaseUrl } = storeToRefs(appSettingsStore);
      const { default: UserApi } = await import("@/services/userApi");
      const userApi = new UserApi(ApiBaseUrl.value, loginCredentials.accessToken);
      const response = await userApi.loginWithThirdPartyAccessToken(loginCredentials);
      await this.processLoginResponse(response, loginCredentials.redirectTo);
    },
    async processLoginResponse(response: ApiResponse, redirectTo: string | null = null) {
      if (response.isSuccess) {
        const appSettingsStore = useAppSettingsStore();
        this.UserInfo = response.data;
        // BR-UI-101 (usermgmt-8-2 hotfix): a gated user must reach /change-password WITHOUT any
        // other authenticated call — the backend 403s everything while MustChangePassword is
        // pending, and getNavbarMenu treats a non-404 failure as fatal (gotoLogout). The navbar
        // bootstrap is deferred to the post-change success path (UpdatePassword.onValidSubmit).
        if (this.IsMustChangePasswordActive) {
          router.push({ name: "ChangePassword" });
          return;
        }
        if (this.IsStudent) {
          const studentStore = useStudentStore();
          await studentStore.getEnrolledCourses(true);
        }
        appSettingsStore.resetMenus();
        await appSettingsStore.getNavbarMenu();
        const { AppMenu } = storeToRefs(appSettingsStore);
        if (AppMenu.value !== null) {
          redirectTo = redirectTo === null ? AppMenu.value.DefaultView : redirectTo;
          router.push({ name: redirectTo });
        }
      } else {
        showCustomMessage({
          messageText: response.errorMessage,
          messageType: "danger",
          duration: 60,
        });
      }
    },
    setUserCommunicationInfo(communicationType: string, communicationValue: string) {
      if (communicationType === LoginIdTypesEnum.EMAIL) {
        this.UserInfo.CommunicationEmail = communicationValue;
      } else if (communicationType === LoginIdTypesEnum.MOBILE) {
        this.UserInfo.CommunicationMobile = communicationValue;
      }
    },
    clearMustChangePassword() {
      // BR-UI-405: on a successful change-password in forced mode, clear the flag locally so
      // IsMustChangePasswordActive releases the gate. Called by ChangePassword.vue (usermgmt-8-2).
      this.UserInfo.MustChangePassword = false;
    },
    async submitB2CRegistration(registerCredentials: RegisterB2CUser) {
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.registerB2CUser(registerCredentials);
      if (response.isSuccess) {
        return { isSuccess: true, message: "Congrats, you have registered successfully." };
      } else {
        return { isSuccess: false, message: response.errorMessage };
      }
    },
    async getUserAlerts() {
      const apiServicesStore = useApiServicesStore();
      const response = await apiServicesStore.appSettingsApi.getUserAlerts();
      this.UserAlerts = response.isSuccess ? (response.data as Array<UserAlert>) : null;
    },
    async sendOtpForLogin(loginId: string | undefined) {
      if (!loginId) {
        showCustomMessage({
          messageText: "Please enter a valid Login Id to login with OTP!",
          messageType: "danger",
        });
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const appSettingsStore = useAppSettingsStore();
      const response = await unverifiedUserApi.value.sendLoginOtp(
        loginId,
        appSettingsStore.AppName,
      );
      if (!(response.isSuccess && response.data)) {
        showCustomMessage({
          messageText: "There was an error sending an OTP to your Login Id!",
          messageType: "danger",
        });
        return;
      }
      return response.data;
    },
    async updateSessionLastAccessed() {
      const isMinTimeElapsed = await this.checkMinimumTimeElapsed(
        this.LastAccessedTime,
        import.meta.env.VITE_LAST_ACCESSED_GAP,
      );
      if (!isMinTimeElapsed) {
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { userApi } = storeToRefs(apiServicesStore);
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      await userApi.value.updateSessionLastAccessed(this.UserInfo.LoginSessionId);
      this.LastAccessedTime = await getServerTimestamp();
    },
    async initDuplicateSessionChecker() {
      if (this.DuplicateSessionInterval !== null) {
        clearInterval(this.DuplicateSessionInterval);
      }
      this.DuplicateSessionInterval = setInterval(
        async () => {
          await this.checkDuplicateLoginSession();
        },
        import.meta.env.VITE_DUPLICATE_LOGIN_CHECK_GAP,
      );
    },
    async checkDuplicateLoginSession() {
      if (!(useAppSettingsStore().IsProductionEnv && this.IsLoggedIn)) {
        return false;
      }
      const isMinTimeElapsed = await this.checkMinimumTimeElapsed(
        this.LastDuplicateSessionChecked,
        import.meta.env.VITE_DUPLICATE_LOGIN_CHECK_GAP,
      );
      if (!isMinTimeElapsed) {
        return false;
      }
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.checkDuplicateLoginSession(
        this.UserInfo.UserId,
        this.UserInfo.LoginSessionId,
      );
      if (response.isSuccess) {
        showCustomMessage({
          messageText: response.data,
          messageType: "danger",
          duration: 60,
          isSticky: true,
        });
        await gotoLogout(true);
        return true;
      }
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      this.LastDuplicateSessionChecked = await getServerTimestamp();
      return false;
    },
    async refreshAccessToken() {
      if (this.UserInfo.RefreshToken !== null) {
        const apiServicesStore = useApiServicesStore();
        const { unverifiedUserApi } = storeToRefs(apiServicesStore);
        const response = await unverifiedUserApi.value.refreshAccessToken(
          this.UserInfo.RefreshToken,
        );
        if (response.isSuccess) {
          this.UserInfo.AccessToken = response.data;
        } else {
          await gotoLogout(true);
        }
      }
      return this.UserInfo.AccessToken;
    },
    async closeIonMenus() {
      const allMenus = await menuController.getMenus();
      if (allMenus.length > 0) {
        for (const menu of allMenus) {
          await menu.close(false);
        }
      }
    },
    hideAppLoader() {
      const loader = document.getElementById("app-loader");
      if (loader) {
        loader.style.display = "none";
      }
    },
    async checkMinimumTimeElapsed(
      lastCheckedTime: number | null | undefined,
      minimumGap: number,
    ): Promise<boolean> {
      if (lastCheckedTime === null || lastCheckedTime === undefined) {
        return true;
      }
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      const serverTimestamp = await getServerTimestamp();
      return serverTimestamp - lastCheckedTime >= minimumGap;
    },
    async resetStore() {
      if (this.DuplicateSessionInterval) {
        clearInterval(this.DuplicateSessionInterval);
      }
      try {
        const offlineDownloadStore = useOfflineDownloadStore();
        await offlineDownloadStore.removeAllDownloads();
      } catch (error) {
        console.warn("Error clearing offline downloads:", error);
      }
      this.$reset();
    },
  },
  persist: true,
});
